import React from 'react';
import type { FunctionComponent } from 'react';
import type { Locales } from '../types';
import { useI18n } from '@whizz/react-i18n';
import { GlobalDashboardLayout } from '../../layouts';
import { TRAINING_MODULES } from '../../util/vars';
import Part1Image from '../../assets/training/part-1.jpg';
import Part2Image from '../../assets/training/part-2.jpg';
import Part3Image from '../../assets/training/part-3.jpg';
import Part4Image from '../../assets/training/part-4.png';

const MODULE_IMAGES: Record<number, string> = {
  1: Part1Image,
  2: Part2Image,
  3: Part3Image,
  4: Part4Image,
};

const Training: FunctionComponent = () => {
  const { translate, locale } = useI18n();

  const country: Locales = (locale?.country as Locales) ?? 'gb';
  const links = TRAINING_MODULES[country] ?? TRAINING_MODULES.gb;

  const modules = links.map((link) => ({
    n: link.n,
    href: link.href,
    image: MODULE_IMAGES[link.n],
    title: translate(`pages.TRAINING.MODULES.PART_${link.n}.TITLE`),
    blurb: translate(`pages.TRAINING.MODULES.PART_${link.n}.BLURB`),
    meta: translate(`pages.TRAINING.MODULES.PART_${link.n}.META`),
    after: translate(`pages.TRAINING.MODULES.PART_${link.n}.AFTER`),
  }));

  const firstModule = modules[0];

  return (
    <GlobalDashboardLayout title={translate('pages.TRAINING.HEADER')} disableContainer>
      <div className={'space-y-10'}>
        <div className={'bg-white rounded-lg shadow p-8 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center'}>
          <div className={'space-y-5'}>
            <div className={'text-sm font-extrabold tracking-widest uppercase text-positive'}>
              {translate('pages.TRAINING.HERO.EYEBROW')}
            </div>
            <h2 className={'text-3xl font-bold text-dark'}>
              {translate('pages.TRAINING.HERO.TITLE')}
            </h2>
            <div className={'grid grid-cols-1 lg:grid-cols-2 gap-5'}>
              <p className={'text-lg text-dark'}>
                {translate('pages.TRAINING.HERO.DESCRIPTION')}
              </p>
              <ul className={'space-y-2'}>
                {[ 'BULLET_1', 'BULLET_2', 'BULLET_3' ].map((bulletKey) => (
                  <li key={bulletKey} className={'flex items-start gap-3'}>
                    <span className={'flex-none w-3 h-3 rounded-full bg-header mt-2'} />
                    <span className={'text-lg font-bold text-dark'}>{translate(`pages.TRAINING.HERO.${bulletKey}`)}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className={'space-y-2 pt-2'}>
              {firstModule?.href ? (
                <a
                  href={firstModule.href}
                  target={'_blank'}
                  rel={'noopener noreferrer'}
                  className={'inline-block w-full lg:w-auto text-center bg-white text-black font-semibold text-lg px-7 py-3 border-3 border-header rounded-lg shadow hover:bg-light'}
                  data-cy={'training-hero-cta'}
                >
                  {translate('pages.TRAINING.HERO.CTA')}
                </a>
              ) : null}
              <div className={'text-sm font-semibold text-dark'}>{translate('pages.TRAINING.HERO.CTA_NOTE')}</div>
            </div>
          </div>
          <div className={'rounded-xl overflow-hidden shadow-lg h-80 bg-primary'}>
            {firstModule ? <img src={firstModule.image} alt={firstModule.title} className={'w-full h-full object-cover'} /> : null}
          </div>
        </div>

        <div className={'space-y-6'}>
          <div className={'flex flex-col md:flex-row md:items-end md:justify-between gap-3'}>
            <h2 className={'text-3xl font-bold text-dark'}>{translate('pages.TRAINING.PATHWAY.TITLE')}</h2>
            <div className={'text-sm font-semibold text-dark'}>{translate('pages.TRAINING.PATHWAY.SUBTITLE')}</div>
          </div>

          <div className={'space-y-4'}>
            {modules.map((training_module) => (
              <div
                key={training_module.n}
                className={'grid grid-cols-1 lg:grid-cols-12 gap-6 items-center bg-white border-2 border-light rounded-2xl p-5'}
                data-cy={`training-module-${training_module.n}`}
              >
                <div className={'relative lg:grid lg:grid-cols-4 lg:gap-6 lg:col-span-4 items-center'}>
                  <div className={'absolute lg:static left-3 -bottom-3 bg-white rounded-lg lg:rounded-none px-3 py-2 lg:p-0 shadow-lg lg:shadow-none flex lg:flex-col items-center justify-center gap-2'}>
                    <div className={'text-sm font-bold tracking-widest text-muted'}>PART</div>
                    <div className={'lg:w-14 lg:h-14 lg:rounded-full lg:bg-header text-header lg:text-dark text-2xl font-extrabold lg:font-bold flex items-center justify-center'}>
                      {training_module.n}
                    </div>
                  </div>
                  <img src={training_module.image} alt={training_module.title} className={'lg:col-span-3 w-full lg:w-64 lg:h-36 object-cover rounded-lg'} style={{ aspectRatio: '16 / 9' }}/>
                </div>
                <div className={'lg:col-span-6 space-y-2'}>
                  <h3 className={'text-xl font-extrabold text-dark'}>{training_module.title}</h3>
                  <p className={' text-dark max-w-2xl'}>{training_module.blurb}</p>
                  <div className={'space-y-2'}>
                    <div className={'text-sm font-bold text-dark'}>{training_module.meta}</div>
                    <div className={'inline-block text-sm text-dark bg-header bg-opacity-20 rounded-md px-3 py-1.5'}>
                      <span className={'font-bold'}>{translate('pages.TRAINING.PATHWAY.NEXT_ACTIONS')}</span> {training_module.after}
                    </div>
                  </div>
                </div>
                {!training_module.href ? (
                  <div
                    className={'lg:col-span-2 justify-self-end w-full text-center bg-light text-muted text-base font-semibold px-5 py-3 border-3 border-light rounded-lg whitespace-nowrap'}
                    data-cy={`training-module-${training_module.n}-coming-soon`}
                  >
                    {translate('pages.TRAINING.COMING_SOON')}
                  </div>
                ) : (
                  <a
                    href={training_module.href}
                    target={'_blank'}
                    rel={'noopener noreferrer'}
                    className={'lg:col-span-2 justify-self-end w-full text-center bg-white text-black text-base font-semibold px-5 py-3 border-3 border-header rounded-lg shadow whitespace-nowrap hover:bg-light'}
                    data-cy={`training-module-${training_module.n}-start`}
                  >
                    {translate('pages.TRAINING.START_PART', { n: training_module.n })}
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </GlobalDashboardLayout>
  );
};

export default Training;
